/**
 * Server state for the Google connection (packages/contracts mail.ts, routes.step8 `google.*`, ADR-003 §9) and the
 * Recordings and privacy settings (whatsapp.ts `PrivacySettings`, `settings.privacy` / `settings.updatePrivacy`):
 *   GET /me/google (my Gmail and Calendar state, the backfill), DELETE /me/google/{scope} (disconnect: Google
 *   revokes BOTH scopes), GET /auth/google/connect (a browser navigation, it answers 302 to Google: a link, not a
 *   fetch), GET /integrations/google + POST /integrations/google/{user_id}/resync (managers' coverage),
 *   PATCH /settings/privacy (If-Match config_version, shared with the other Settings groups).
 * Never any token material: the API does not send it and nothing here asks for it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  buildPath,
  routes,
  type GoogleConnectionState,
  type GoogleScope,
  type MyGoogleConnection,
  type PatchPrivacySettingsBody,
} from '@amplispot/contracts';
import { call } from '../api';
import { mkeys as meetingKeys } from './meetings';
import { refreshMail } from './mail';

export const gkeys = {
  me: ['google', 'me'] as const,
  coverage: ['google', 'coverage'] as const,
};

/** Where Google sends people back to; the API's own default (GOOGLE_DEFAULT_REDIRECT) is this tab. */
export const CONNECTIONS_PATH = '/settings/connections';

/** GET /auth/google/connect?scope=…&redirect=…, read-only Gmail or Calendar. */
export const googleConnectHref = (scope: GoogleScope, redirect = CONNECTIONS_PATH): string =>
  `${buildPath(routes['google.connect'].path)}?${new URLSearchParams({ scope, redirect }).toString()}`;

export function useMyGoogle(enabled = true) {
  // While the 90-day history is being read, look again every 15 s so "Reading…" turns into "Done" by itself.
  return useQuery({
    queryKey: gkeys.me,
    queryFn: () => call('google.me'),
    enabled,
    refetchInterval: (q) => (q.state.data?.backfill === 'running' ? 15_000 : false),
  });
}

export function useGoogleCoverage(enabled = true) {
  return useQuery({ queryKey: gkeys.coverage, queryFn: () => call('google.coverage'), enabled });
}

/** DELETE /me/google/{scope}. Google revokes both scopes at once, so the response carries both states. */
export function useDisconnectGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scope: GoogleScope) => call('google.disconnect', { params: { scope } }),
    onSuccess: (r) => {
      qc.setQueryData(gkeys.me, r.connection);
      void qc.invalidateQueries({ queryKey: gkeys.coverage });
      refreshMail(qc);
    },
  });
}

/** POST /integrations/google/{user_id}/resync: poll that person's mailbox and calendar now (409 without a grant). */
export function useResyncGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => call('google.resync', { params: { user_id: userId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: gkeys.coverage }),
  });
}

/** PATCH /settings/privacy with If-Match. The Meetings form reads the same query, so it follows at once. */
export function useUpdatePrivacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { version: number; body: PatchPrivacySettingsBody }) => call('settings.updatePrivacy', { body: v.body, ifMatch: v.version }),
    onSuccess: (r) => qc.setQueryData(meetingKeys.privacy, r),
  });
}

// ---------------------------------------------------------------- display helpers

/** Prototype chips: "Connected" hot (as Zoho billing on Settings → Integrations); a broken grant cold; never granted plain. */
export const GOOGLE_STATE_CHIP: Record<GoogleConnectionState, [cls: string, label: string]> = {
  connected: ['hot', 'Connected'],
  not_connected: ['', 'Not connected'],
  needs_reconnect: ['cold', 'Connect again'],
  admin_blocked: ['warm', 'Blocked by your administrator'],
  revoked: ['', 'Disconnected'],
};

export const BACKFILL_LABEL: Record<MyGoogleConnection['backfill'], string | null> = {
  none: null,
  running: 'Reading the last 90 days of your client emails. This takes a few minutes.',
  done: 'The last 90 days of your client emails have been read.',
  failed: 'Reading the last 90 days of your client emails did not finish.',
};

/** Retention choices (AudioRetentionDays); `null` is forever, the default. */
export const RETENTION_OPTIONS: [value: 30 | 90 | 180 | null, label: string][] = [
  [30, '30 days'],
  [90, '90 days'],
  [180, '180 days'],
  [null, 'Forever'],
];
